import { createPersistedStore } from '@tasama/utils';
import type { BaseStoreState } from '@tasama/utils';
import { i18n } from '@tasama/i18n';
import {
  DEFAULT_ATTEMPT_SERVICE_TYPE,
  type AttemptServiceType,
} from '../types/attempt-service-type';

/**
 * Upload file shape for PO - mirrors SCSignScreen.UploadedFile pattern
 */
export interface POUploadedFile {
  id: string;
  fileName: string;
  fileSize: string;
  bytes?: number;
  progress: number;
  status: 'uploading' | 'complete' | 'error';
  file?: File;
}

interface POUploadState extends BaseStoreState {
  uploadedFiles: POUploadedFile[];
  srAttemptId: string;
  rejectionReason: string | null;
  serviceType: AttemptServiceType;
  setSrAttemptId: (_srAttemptId: string) => void;
  setServiceType: (_serviceType: AttemptServiceType) => void;
  setRejectionReason: (_reason: string | null) => void;
  setUploadedFiles: (_files: POUploadedFile[]) => void;
  addFile: (_file: POUploadedFile) => void;
  updateFile: (_fileId: string, _updates: Partial<POUploadedFile>) => void;
  removeFile: (_fileId: string) => void;
  clearFiles: () => void;
  reset: () => void;
  setUploadError: (_error: string | null) => void;
  validateFiles: () => { success: boolean; error?: string };
  getFormData: () => FormData | null;
}

const initialFormData: Omit<POUploadState, keyof BaseStoreState> = {
  uploadedFiles: [],
  srAttemptId: '',
  rejectionReason: null,
  serviceType: DEFAULT_ATTEMPT_SERVICE_TYPE,
  setSrAttemptId: () => {},
  setServiceType: () => {},
  setRejectionReason: () => {},
  setUploadedFiles: () => {},
  addFile: () => {},
  updateFile: () => {},
  removeFile: () => {},
  clearFiles: () => {},
  reset: () => {},
  setUploadError: () => {},
  validateFiles: () => ({ success: false }),
  getFormData: () => null,
};

export const usePOUploadStore = createPersistedStore<POUploadState>(
  'po-upload',
  initialFormData,
  (set, get) => ({
    setSrAttemptId: (srAttemptId: string) => {
      set({ srAttemptId } as Partial<POUploadState>);
    },

    setServiceType: (serviceType: AttemptServiceType) => {
      set({ serviceType } as Partial<POUploadState>);
    },

    setRejectionReason: (rejectionReason: string | null) => {
      set({ rejectionReason } as Partial<POUploadState>);
    },

    setUploadedFiles: (files: POUploadedFile[]) => {
      set({ uploadedFiles: files } as Partial<POUploadState>);
    },

    addFile: (file: POUploadedFile) => {
      const { uploadedFiles } = get();
      set({
        uploadedFiles: [...uploadedFiles, file],
      } as Partial<POUploadState>);
    },

    updateFile: (fileId: string, updates: Partial<POUploadedFile>) => {
      const { uploadedFiles } = get();
      const updatedFiles = uploadedFiles.map((file: POUploadedFile) =>
        file.id === fileId ? { ...file, ...updates } : file
      );
      set({ uploadedFiles: updatedFiles } as Partial<POUploadState>);
    },

    removeFile: (fileId: string) => {
      const { uploadedFiles } = get();
      set({
        uploadedFiles: uploadedFiles.filter(
          (f: POUploadedFile) => f.id !== fileId
        ),
      } as Partial<POUploadState>);
    },

    clearFiles: () => {
      set({ uploadedFiles: [] } as Partial<POUploadState>);
    },

    reset: () => {
      set({
        uploadedFiles: [],
        loading: false,
        error: null,
      } as Partial<POUploadState>);
    },

    setUploadError: (error: string | null) => {
      set({ error } as Partial<POUploadState>);
    },

    validateFiles: () => {
      const { uploadedFiles } = get();

      if (uploadedFiles.length === 0) {
        return {
          success: false,
          error: i18n.t('purchaseOrder.noFileSelected', { ns: 'portal' }),
        };
      }

      const file = uploadedFiles[0];

      // Max 5 MB
      if (file.bytes && file.bytes > 5 * 1024 * 1024) {
        return {
          success: false,
          error: i18n.t('purchaseOrder.fileTooLarge', { ns: 'portal' }),
        };
      }

      return { success: true };
    },

    getFormData: () => {
      const { uploadedFiles, srAttemptId, serviceType } = get();
      const uploadFile = uploadedFiles[0];

      if (!uploadFile?.file) {
        return null;
      }

      const formData = new FormData();
      const idField =
        serviceType === 'bundle' ? 'bundleAttemptId' : 'srAttemptId';
      formData.append(idField, srAttemptId);
      formData.append('file', uploadFile.file);
      return formData;
    },
  }),
  {
    partialize: (state) => ({
      srAttemptId: state.srAttemptId,
      serviceType: state.serviceType,
      uploadedFiles: state.uploadedFiles.map((file: POUploadedFile) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
        const { file: _file, bytes, ...rest } = file;
        return rest;
      }),
    }),
    clearOnRefresh: ['uploadedFiles'],
  }
);

// Selectors
export const usePOUploadFiles = () =>
  usePOUploadStore((state) => state.uploadedFiles);
export const usePOUploadIsUploading = () =>
  usePOUploadStore((state) => state.loading);
export const usePOUploadError = () => usePOUploadStore((state) => state.error);
